import type { ReactNode } from 'react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { cn } from '../cn';
import Button from '../Input2/Button';
import Input from '../Input2/Input';
import Tooltip from '../Input2/Tooltip';
import { Alerts, Arrows, Communication, General, Media, Security } from '../StrokeIcons';

export type ContactFieldReadOnlyValue = {
  data: string;
  source: string;
  primary: boolean;
  verified?: boolean | null;
  /** Why the value is invalid (e.g. 'firm_number', 'wrong_number', 'do_not_call'), when known.
      Drives the invalid-reason badge. */
  invalidReason?: string | null;
  /** Who condemned the value (user id) — resolved to a display name via `userNamesById`. */
  invalidMarkedByUserId?: string | null;
  /** When the value was condemned; shown in the value tooltip. */
  invalidMarkedAt?: Date | string | null;
  /** Twilio Lookup line type ('mobile', 'landline', …) — set only on phone values checked by the
      re-enrichment waterfall's validation gate. `'mobile'` renders the emerald "Mobile · validated"
      badge; other checked types render a neutral badge ("VoIP", "Landline"). */
  lineType?: string | null;
  /** The Twilio Lookup evidence behind `lineType` — shown as the badge's tooltip so the user can
      see what justified the validation. Absent on values that predate evidence storage. */
  phoneLookup?: ContactFieldPhoneLookup | null;
  /** When the number was last dialed (phone values only) — drives the "Dialed …" affordance so
      the sidebar agrees with the Bad Numbers page about a number's call history. */
  lastDialedAt?: Date | string | null;
  /** NeverBounce verdict on an email value ('valid', 'catchall', 'invalid', 'disposable',
      'unknown', 'disabled') — drives the deliverability badge. This is WHY enrichment does or
      doesn't elect a default email, so it must be visible, not just stored. */
  emailVerificationCode?: string | null;
  confidenceScore?: number | null;
  createdAt?: Date | string | null;
};

/** Mirrors `SvrPhoneLookup` (declared locally — this package doesn't depend on server types). */
export type ContactFieldPhoneLookup = {
  checkedAt: string | null;
  valid: boolean | null;
  lineType: string | null;
  lineStatus: string | null;
  carrierName: string | null;
};

/** Order for a field's "other values": condemned ones (`verified === false`) sink below the
    usable candidates. Stable — apart from that, input order is preserved. */
export function sortCondemnedLast<T extends Pick<ContactFieldReadOnlyValue, 'verified'>>(values: T[]): T[] {
  return [...values].sort((a, b) => Number(a.verified === false) - Number(b.verified === false));
}

/** Human label for an `invalidReason`, or null when there's nothing to show. */
export function invalidReasonLabel(reason: string | null | undefined): string | null {
  if (reason === 'firm_number') return 'Firm number';
  if (reason === 'wrong_number') return 'Wrong number';
  if (reason === 'do_not_call') return 'Do not call';
  return null;
}

/** Invalid-reason badge for a condemned value (`verified === false`) — a compact red icon,
    keeping the row narrow so the number itself doesn't truncate. Deliberately inert: the
    surrounding value row carries the info tooltip (a far bigger hover target than the icon). */
function InvalidReasonBadge({ field }: { field: Pick<ContactFieldReadOnlyValue, 'verified' | 'invalidReason'> }) {
  if (field.verified !== false) return null;
  const label = invalidReasonLabel(field.invalidReason);
  if (label == null) return null;
  return (
    <span aria-label={label} className="size-3.5 shrink-0 text-red-600 dark:text-red-400 [&>svg]:size-full">
      {field.invalidReason === 'firm_number' ? (
        <General.Building02 />
      ) : field.invalidReason === 'do_not_call' ? (
        <Alerts.AlertTriangle />
      ) : (
        <Communication.PhoneHangUp02 />
      )}
    </span>
  );
}

/**
 * Line-type badge for a Lookup-checked value. "Mobile · validated" (emerald) for an active mobile —
 * distinct from human-verified green: `verified` keeps its meaning of human-confirmed. Other checked
 * line types get a neutral label ("VoIP", "Landline") so a checked-but-not-mobile number doesn't
 * render identically to unvetted data. Null when unchecked or condemned (`verified === false` —
 * the invalid-reason badge takes over there).
 */
export function lineTypeBadgeLabel(field: Pick<ContactFieldReadOnlyValue, 'lineType' | 'verified'>): string | null {
  if (field.verified === false) return null;
  if (field.lineType == null || field.lineType === 'unknown') return null;
  if (field.lineType === 'mobile') return 'Mobile · validated';
  if (field.lineType === 'landline') return 'Landline';
  if (field.lineType === 'fixedvoip' || field.lineType === 'nonfixedvoip') return 'VoIP';
  return field.lineType;
}

/**
 * The justification behind the "Mobile · validated" badge, e.g.
 * "Checked Aug 5, 2026 · Verizon Wireless". Deliberately does NOT name the lookup
 * provider — that's internal plumbing, not something to expose to the user. Nor does
 * it repeat "valid" / "active" / the line type: the waterfall gate drops invalid and
 * inactive numbers before storing, so stored evidence is always a valid active line,
 * and the status headline already names the type — only anomalies (invalid / non-active
 * per stored data) are worth words. Null when the value has no stored Lookup evidence
 * (rows predating evidence storage).
 */
export function lineTypeBadgeTooltip(field: Pick<ContactFieldReadOnlyValue, 'phoneLookup'>): string | null {
  const lookup = field.phoneLookup;
  if (lookup == null) return null;
  const parts: string[] = [];
  if (lookup.valid === false) parts.push('invalid number');
  if (lookup.lineStatus != null && lookup.lineStatus !== 'active') parts.push(`${lookup.lineStatus} line`);
  if (lookup.carrierName != null) parts.push(lookup.carrierName);
  const checked = lookup.checkedAt != null ? DateTime.fromISO(lookup.checkedAt) : null;
  const checkedLabel = checked?.isValid ? ` ${checked.toLocaleString(DateTime.DATE_MED)}` : '';
  if (parts.length === 0) return checkedLabel === '' ? null : `Checked${checkedLabel}`;
  return `Checked${checkedLabel} · ${parts.join(' · ')}`;
}

/** "Dialed 2 days ago" — the value's call history, or null when never dialed. */
export function dialedLabel(field: Pick<ContactFieldReadOnlyValue, 'lastDialedAt'>): string | null {
  const ago = formatUpdated(field.lastDialedAt);
  return ago == null ? null : `Dialed ${ago}`;
}

/**
 * Deliverability verdict behind an email value's badge — the email counterpart of
 * `lineTypeBadgeLabel`. 'Deliverable' (emerald) only for a NeverBounce-confirmed mailbox;
 * 'Catch-all' (amber) for a domain that accepts everything (unverifiable — which is exactly
 * why enrichment won't auto-default to it); red tones for confirmed-bad answers. Null when
 * never checked, or when condemned (`verified === false` — the invalid badge owns that row).
 */
export function emailVerificationBadge(
  field: Pick<ContactFieldReadOnlyValue, 'emailVerificationCode' | 'verified'>,
): { label: string; tone: 'good' | 'risky' | 'bad' | 'neutral'; description: string } | null {
  if (field.verified === false) return null;
  switch (field.emailVerificationCode) {
    case 'valid':
      return { label: 'Deliverable', tone: 'good', description: 'Mailbox confirmed to exist' };
    case 'catchall':
      return {
        label: 'Catch-all',
        tone: 'risky',
        description: 'Domain accepts all addresses — mailbox unverifiable, may bounce',
      };
    case 'invalid':
      return { label: 'Undeliverable', tone: 'bad', description: 'Mailbox confirmed not to exist' };
    case 'disposable':
      return { label: 'Disposable', tone: 'bad', description: 'Temporary throwaway address' };
    case 'disabled':
      return { label: 'Disabled', tone: 'bad', description: 'Mailbox exists but is disabled' };
    case 'unknown':
      return { label: 'Unverifiable', tone: 'neutral', description: 'Mail server could not be reached' };
    default:
      return null;
  }
}

const EMAIL_BADGE_TONE_TEXT = {
  good: 'text-emerald-600 dark:text-emerald-400',
  risky: 'text-amber-600 dark:text-amber-400',
  bad: 'text-red-600 dark:text-red-400',
  neutral: 'text-slate-400 dark:text-slate-500',
} as const;

const EMAIL_BADGE_TONE_TOOLTIP_TEXT = {
  good: 'text-emerald-400',
  risky: 'text-amber-400',
  bad: 'text-red-400',
  neutral: 'opacity-60',
} as const;

/** The deliverability badge — a compact shield icon colored by verdict. Deliberately inert:
    the surrounding value row carries the info tooltip (a far bigger hover target). */
function EmailVerificationBadge({
  field,
}: {
  field: Pick<ContactFieldReadOnlyValue, 'emailVerificationCode' | 'verified'>;
}) {
  const badge = emailVerificationBadge(field);
  if (badge == null) return null;
  return (
    <span
      aria-label={badge.label}
      className={cn('size-3.5 shrink-0 [&>svg]:size-full', EMAIL_BADGE_TONE_TEXT[badge.tone])}>
      <Security.Shield01 />
    </span>
  );
}

/** Already-dialed marker — a compact outgoing-phone icon on values with call history, so a rep
    can tell a fresh number from one that's been tried without opening the Bad Numbers page.
    Suppressed on condemned values (`verified === false`): the invalid-reason badge owns that row's
    story, and its tooltip still carries the dial history. Deliberately inert: the surrounding
    value row carries the info tooltip (a far bigger hover target than the icon). */
function DialedBadge({ field }: { field: Pick<ContactFieldReadOnlyValue, 'lastDialedAt' | 'verified'> }) {
  if (field.verified === false) return null;
  const label = dialedLabel(field);
  if (label == null) return null;
  return (
    <span aria-label={label} className="size-3.5 shrink-0 text-slate-400 dark:text-slate-500 [&>svg]:size-full">
      <Communication.PhoneOutgoing01 />
    </span>
  );
}

/** The line-type badge (emerald for mobile, neutral for other checked line types) — a compact
    icon. Deliberately inert: the surrounding value row carries the info tooltip (a far bigger
    hover target than the icon). */
function LineTypeBadge({ field }: { field: Pick<ContactFieldReadOnlyValue, 'lineType' | 'verified' | 'phoneLookup'> }) {
  const label = lineTypeBadgeLabel(field);
  if (label == null) return null;
  const isVoip = field.lineType === 'fixedvoip' || field.lineType === 'nonfixedvoip';
  return (
    <span
      aria-label={label}
      className={cn(
        'size-3.5 shrink-0 [&>svg]:size-full',
        field.lineType === 'mobile' && 'text-emerald-600 dark:text-emerald-400',
        field.lineType !== 'mobile' && 'text-slate-400 dark:text-slate-500',
      )}>
      {field.lineType === 'mobile' ? <Media.Phone01 /> : isVoip ? <Media.Signal01 /> : <Communication.Phone />}
    </span>
  );
}

/** Hover-card content for one value: status (invalid reason / line type / human verdict), the
    Lookup evidence behind it, and provenance. One tooltip per value row — anchored on the whole
    number, not the tiny status icons. */
function ValueInfo({
  field,
  fmt,
  withData,
  userNamesById,
}: {
  field: ContactFieldReadOnlyValue;
  fmt: (v: string) => string;
  /** Lead with the (formatted) value itself — used where the tooltip isn't anchored on a
      visible copy of the value, e.g. the collapsed-field preview. */
  withData?: boolean;
  /** Resolves `invalidMarkedByUserId` to a display name for the "Marked by …" line. */
  userNamesById?: Record<string, string>;
}) {
  const reason = field.verified === false ? (invalidReasonLabel(field.invalidReason) ?? 'Invalid') : null;
  const lineLabel = reason == null ? lineTypeBadgeLabel(field) : null;
  const emailBadge = reason == null ? emailVerificationBadge(field) : null;
  const evidence = lineTypeBadgeTooltip(field);
  const dialed = dialedLabel(field);
  const updated = formatUpdated(field.createdAt);
  const source = sourceLabel(field.source);
  // "From Enriched" is broken English — that label stands on its own.
  const provenance = source === 'Enriched' ? source : `From ${source}`;
  const markedBy = field.invalidMarkedByUserId != null ? userNamesById?.[field.invalidMarkedByUserId] : null;
  const markedAt = formatUpdated(field.invalidMarkedAt);
  return (
    <div className="flex flex-col gap-0.5 normal-case tracking-normal">
      {withData && <span className="font-medium">{fmt(field.data)}</span>}
      {reason != null && <span className="font-semibold text-red-400">{reason}</span>}
      {reason != null && (markedBy != null || markedAt != null) && (
        <span className="opacity-70">
          Marked
          {markedBy != null && ` by ${markedBy}`}
          {markedAt != null && ` · ${markedAt}`}
        </span>
      )}
      {lineLabel != null && (
        <span className={cn('font-semibold', field.lineType === 'mobile' && 'text-emerald-400')}>{lineLabel}</span>
      )}
      {emailBadge != null && (
        <>
          <span className={cn('font-semibold', EMAIL_BADGE_TONE_TOOLTIP_TEXT[emailBadge.tone])}>
            {emailBadge.label}
          </span>
          <span className="opacity-70">{emailBadge.description}</span>
        </>
      )}
      {reason == null && lineLabel == null && emailBadge == null && field.verified === true && (
        <span className="font-semibold text-green-400">Valid</span>
      )}
      {evidence != null && <span className="opacity-70">{evidence}</span>}
      {dialed != null && <span className="opacity-70">{dialed}</span>}
      <span className="opacity-70">
        {provenance}
        {updated != null && ` · ${updated}`}
      </span>
    </div>
  );
}

export type ContactFieldReadOnlyProps = {
  title: string;
  icon?: ReactNode;
  fields?: ContactFieldReadOnlyValue[];
  onRemovePrimary?: (source: string) => void;
  onMakePrimary?: (source: string) => void;
  onDelete?: (source: string) => void;
  /** When provided, shows a verify icon next to each value. */
  onVerify?: (source: string) => void | Promise<void>;
  /** When provided (phone field), shows a "Mark as firm number" action next to each value that
      isn't already flagged as one. Receives the phone number (the value's `data`). */
  onMarkFirmNumber?: (phone: string) => void | Promise<void>;
  /** Adds a new value with source 'input'. When provided, the + button is shown. */
  onAdd?: (value: string) => void | Promise<void>;
  /** Controlled expanded state. If omitted, expansion is managed internally. */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  /** Controlled adding state. If omitted, adding is managed internally. */
  isAdding?: boolean;
  onIsAddingChange?: (next: boolean) => void;
  /** When true, the expanded panel is NOT rendered inline. The parent is expected
      to render it (e.g. as a full-width sibling below a grid). */
  hideInlineExpandedPanel?: boolean;
  /** Formats each value purely for display (e.g. phone numbers). The underlying
      data is untouched — only the rendered string changes. Defaults to identity. */
  formatValue?: (value: string) => string;
  /** Resolves user ids (e.g. who condemned a value) to display names for tooltips. */
  userNamesById?: Record<string, string>;
};

export function sourceLabel(source: string): string {
  if (source.startsWith('enhance') || source.startsWith('enrich')) return 'Enrichment';
  if (source.startsWith('basic-enhance') || source.startsWith('basic-enrich')) return 'Basic enrichment';
  if (source === 'linkedin-api') return 'LinkedIn (API)';
  if (source === 'linkedin-electron') return 'LinkedIn (Electron)';
  if (source.startsWith('linkedin')) return 'LinkedIn';
  if (source.startsWith('crm')) return 'CRM';
  if (source.startsWith('input')) return 'Manual input';
  if (source.startsWith('csv')) return 'CSV upload';
  if (source.startsWith('ai')) return 'AI';
  if (source.startsWith('close')) return 'Close';
  // Phone re-enrichment waterfall value-keyed sources ('<provider>:<E.164>').
  // The vendor behind an enriched value is internal plumbing — never name it to
  // the user, just say the value came from enrichment.
  if (source.startsWith('wiza:')) return 'Enriched';
  if (source.startsWith('pdl:')) return 'Enriched';
  if (source.startsWith('oracle:')) return 'Shared record';
  // Historical wrong-number marks reconstructed by the backfill migration: the
  // original row (and with it the acquisition source) was hard-deleted by the
  // pre-waterfall code, so the true source is unknowable — say so rather than
  // implying a data source we don't use.
  if (source.startsWith('backfill-wrong-number:')) return 'Unknown';
  return source;
}

function formatUpdated(value: Date | string | null | undefined): string | null {
  if (value == null) return null;
  const dt = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(value);
  if (!dt.isValid) return null;
  return dt.toRelative();
}

export function ContactFieldAddRow({
  onSave,
  onCancel,
}: {
  onSave: (value: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onSave(trimmed);
      setValue('');
      onCancel();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-stretch py-0.5">
      <Input
        size="sm"
        autoFocus
        placeholder="New value..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSave();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        className="flex-1 rounded-r-none border-r-0"
        disabled={saving}
      />
      <Button
        size="sm"
        color="green"
        icon={<General.CheckCircle />}
        tooltip="Save"
        onClick={() => void handleSave()}
        disabled={saving || value.trim().length === 0}
        className="rounded-l-none"
      />
    </div>
  );
}

export function ContactFieldExpandedRows({
  others,
  onMakePrimary,
  onDelete,
  onVerify,
  onMarkFirmNumber,
  addRow,
  formatValue,
  userNamesById,
}: {
  others: ContactFieldReadOnlyValue[];
  onMakePrimary?: (source: string) => void;
  onDelete?: (source: string) => void;
  onVerify?: (source: string) => void | Promise<void>;
  onMarkFirmNumber?: (phone: string) => void | Promise<void>;
  addRow?: ReactNode;
  formatValue?: (value: string) => string;
  /** Resolves user ids (e.g. who condemned a value) to display names for tooltips. */
  userNamesById?: Record<string, string>;
}) {
  const fmt = formatValue ?? ((v: string) => v);
  return (
    <div className="flex flex-col">
      {addRow}
      {others.map((f, i) => (
        <div
          key={String(i) + f.source + f.data}
          className="group/row flex items-center gap-1 text-sm text-neutral-600 dark:text-neutral-200">
          <Tooltip
            tooltip={<ValueInfo field={f} fmt={fmt} userNamesById={userNamesById} />}
            tooltipPosition="left"
            tooltipFallbacks="top, bottom, flip-inline"
            tooltipMaxWidth={260}
            className="flex min-w-0 items-center gap-1">
            <p
              className={cn(
                'min-w-0 truncate',
                f.verified === true && 'text-green-600 dark:text-green-400',
                f.verified === false && 'text-red-600 dark:text-red-400',
              )}>
              {fmt(f.data)}
            </p>
            <InvalidReasonBadge field={f} />
            <LineTypeBadge field={f} />
            <EmailVerificationBadge field={f} />
            <DialedBadge field={f} />
            <span className="shrink-0 text-[10px] uppercase tracking-wide text-haiti-500 dark:text-haiti-400">
              {sourceLabel(f.source)}
            </span>
          </Tooltip>
          <div className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100">
            {onMakePrimary && (
              <Button
                size="xs"
                color="green"
                text
                noPadding
                icon={<General.CheckCircle />}
                tooltip="Make primary"
                onClick={() => onMakePrimary(f.source)}
              />
            )}
            {onVerify && (
              <Button
                size="xs"
                color="blue"
                text
                noPadding
                icon={<Security.Shield01 />}
                tooltip={
                  f.verified === true ? 'Re-verify (valid)' : f.verified === false ? 'Re-verify (invalid)' : 'Verify'
                }
                onClick={() => void onVerify(f.source)}
              />
            )}
            {onMarkFirmNumber && f.invalidReason !== 'firm_number' && (
              <Button
                size="xs"
                color="yellow"
                text
                noPadding
                icon={<General.Building02 />}
                tooltip="Mark as firm number"
                onClick={() => void onMarkFirmNumber(f.data)}
              />
            )}
            {onDelete && (
              <Button
                size="xs"
                color="red"
                text
                noPadding
                icon={<General.Trash01 />}
                tooltip="Delete"
                onClick={() => onDelete(f.source)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ContactFieldReadOnly({
  title,
  icon,
  fields,
  onRemovePrimary,
  onMakePrimary,
  onDelete,
  onVerify,
  onMarkFirmNumber,
  onAdd,
  expanded: expandedProp,
  onExpandedChange,
  isAdding: isAddingProp,
  onIsAddingChange,
  hideInlineExpandedPanel,
  formatValue,
  userNamesById,
}: ContactFieldReadOnlyProps) {
  const fmt = formatValue ?? ((v: string) => v);
  const primary = fields?.find((f) => f.primary);
  const others = sortCondemnedLast(fields?.filter((f) => f !== primary) ?? []);
  const otherCount = others.length;
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp ?? internalExpanded;
  const setExpanded = (next: boolean) => {
    if (onExpandedChange) onExpandedChange(next);
    if (expandedProp === undefined) setInternalExpanded(next);
  };
  const [internalIsAdding, setInternalIsAdding] = useState(false);
  const isAdding = isAddingProp ?? internalIsAdding;
  const setIsAdding = (next: boolean) => {
    if (onIsAddingChange) onIsAddingChange(next);
    if (isAddingProp === undefined) setInternalIsAdding(next);
  };

  // Begin (or cancel) adding a value. Shared by the "+" button and the
  // empty-state placeholder so clicking either does the same thing.
  const handleToggleAdd = () => {
    if (isAdding) {
      setIsAdding(false);
    } else {
      setIsAdding(true);
      setExpanded(true);
    }
  };

  // If the last "other value" gets deleted while we're expanded (and not in
  // the middle of adding), auto-collapse the field.
  useEffect(() => {
    if (expanded && otherCount === 0 && !isAdding) {
      setExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, otherCount, isAdding]);

  const tooltipContent =
    primary || otherCount > 0 ? (
      <div className="flex flex-col gap-2 normal-case tracking-normal">
        {primary && <ValueInfo field={primary} fmt={fmt} withData userNamesById={userNamesById} />}
        {otherCount > 0 && (
          <div className="flex flex-col gap-0.5">
            <span className="opacity-70">
              {otherCount} other possible value{otherCount === 1 ? '' : 's'}:
            </span>
            {others.map((f, i) => {
              const emailBadge = emailVerificationBadge(f);
              return (
                <span key={String(i) + f.source + f.data}>
                  {fmt(f.data)} <span className="opacity-60">— {sourceLabel(f.source)}</span>
                  {f.verified === true && <span className="ml-1 text-green-400">· Valid</span>}
                  {f.verified === false && (
                    <span className="ml-1 text-red-400">· {invalidReasonLabel(f.invalidReason) ?? 'Invalid'}</span>
                  )}
                  {f.verified !== false && lineTypeBadgeLabel(f) != null && (
                    <span className={cn('ml-1', f.lineType === 'mobile' ? 'text-emerald-400' : 'opacity-60')}>
                      · {lineTypeBadgeLabel(f)}
                    </span>
                  )}
                  {emailBadge != null && (
                    <span className={cn('ml-1', EMAIL_BADGE_TONE_TOOLTIP_TEXT[emailBadge.tone])}>
                      · {emailBadge.label}
                    </span>
                  )}
                  {dialedLabel(f) != null && <span className="ml-1 opacity-60">· {dialedLabel(f)}</span>}
                </span>
              );
            })}
          </div>
        )}
      </div>
    ) : undefined;

  return (
    <Tooltip
      tooltip={expanded ? undefined : tooltipContent}
      tooltipPosition="left"
      tooltipFallbacks="top, bottom, flip-inline"
      tooltipMaxWidth={260}
      className={cn(
        'block',
        expanded && [
          'relative isolate',
          "before:absolute before:inset-x-2 before:inset-y-0 before:-z-10 before:content-['']",
          'before:bg-slate-100 dark:before:bg-slate-800/40',
          hideInlineExpandedPanel ? 'before:rounded-t-xl' : 'before:rounded-xl',
        ],
      )}>
      <div className={cn('group/field flex flex-col px-4 pt-2.5', expanded && !hideInlineExpandedPanel ? '' : 'pb-1')}>
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-haiti-500 dark:text-haiti-400">
            {icon && <div className="size-3 shrink-0 text-gray-400 dark:text-gray-500 [&>svg]:size-full">{icon}</div>}
            <span>{title}</span>
            {otherCount > 0 && <span>({otherCount})</span>}
            <div className="flex items-center gap-0.5">
              {otherCount > 0 && (
                <Button
                  size="xs"
                  text
                  noPadding
                  icon={<Arrows.ChevronDown className={cn('transition-transform', expanded && 'rotate-180')} />}
                  tooltip={expanded ? 'Hide other values' : 'Show other values'}
                  onClick={() => setExpanded(!expanded)}
                  className={cn('transition-opacity', !expanded && 'opacity-0 group-hover/field:opacity-100')}
                />
              )}
              {onAdd && (
                <Button
                  size="xs"
                  color={isAdding ? 'red' : 'gray'}
                  text
                  noPadding
                  icon={isAdding ? <General.XCircle /> : <General.Plus />}
                  tooltip={isAdding ? 'Cancel' : 'Add'}
                  onClick={handleToggleAdd}
                  className={cn('transition-opacity', !isAdding && 'opacity-0 group-hover/field:opacity-100')}
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 text-base font-medium text-neutral-600 dark:text-neutral-100">
            {primary && (
              <>
                {/* While collapsed, the whole field is the tooltip target (see the outer
                    Tooltip); this inner one takes over when expanded, so the primary row
                    behaves like the expanded rows below it — and never double-fires. */}
                <Tooltip
                  tooltip={expanded ? <ValueInfo field={primary} fmt={fmt} userNamesById={userNamesById} /> : undefined}
                  tooltipPosition="left"
                  tooltipFallbacks="top, bottom, flip-inline"
                  tooltipMaxWidth={260}
                  className="flex min-w-0 items-center gap-1">
                  <p
                    className={cn(
                      'min-w-0 truncate',
                      primary.verified === true && 'text-green-600 dark:text-green-400',
                      primary.verified === false && 'text-red-600 dark:text-red-400',
                    )}>
                    {fmt(primary.data)}
                  </p>
                  <InvalidReasonBadge field={primary} />
                  <LineTypeBadge field={primary} />
                  <EmailVerificationBadge field={primary} />
                  <DialedBadge field={primary} />
                </Tooltip>
                {onMarkFirmNumber && primary.invalidReason !== 'firm_number' && (
                  <Button
                    size="xs"
                    color="yellow"
                    text
                    noPadding
                    icon={<General.Building02 />}
                    tooltip="Mark as firm number"
                    onClick={() => void onMarkFirmNumber(primary.data)}
                    className="opacity-0 transition-opacity group-hover/field:opacity-100"
                  />
                )}
                {onVerify && (
                  <Button
                    size="xs"
                    color="blue"
                    text
                    noPadding
                    icon={<Security.Shield01 />}
                    tooltip={
                      primary.verified === true
                        ? 'Re-verify (valid)'
                        : primary.verified === false
                          ? 'Re-verify (invalid)'
                          : 'Verify'
                    }
                    onClick={() => void onVerify(primary.source)}
                    className="opacity-0 transition-opacity group-hover/field:opacity-100"
                  />
                )}
                {onRemovePrimary && (
                  <Button
                    size="xs"
                    color="red"
                    text
                    noPadding
                    icon={<General.XCircle />}
                    tooltip="Remove default"
                    onClick={() => onRemovePrimary(primary.source)}
                    className="opacity-0 transition-opacity group-hover/field:opacity-100"
                  />
                )}
              </>
            )}
            {!primary && onAdd && (
              <Button
                text
                size="lg"
                tooltip={isAdding ? 'Cancel' : 'Add'}
                onClick={handleToggleAdd}
                className="h-auto w-auto min-w-0 cursor-pointer truncate px-0 font-medium text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300">
                unknown
              </Button>
            )}
            {!primary && !onAdd && <p className="min-w-0 truncate text-slate-400 dark:text-slate-500">unknown</p>}
          </div>
          {expanded && (otherCount > 0 || isAdding) && !hideInlineExpandedPanel && (
            <ContactFieldExpandedRows
              others={others}
              onMakePrimary={onMakePrimary}
              onDelete={onDelete}
              onVerify={onVerify}
              onMarkFirmNumber={onMarkFirmNumber}
              formatValue={formatValue}
              userNamesById={userNamesById}
              addRow={
                isAdding && onAdd ? (
                  <ContactFieldAddRow onSave={onAdd} onCancel={() => setIsAdding(false)} />
                ) : undefined
              }
            />
          )}
        </div>
      </div>
    </Tooltip>
  );
}
